// ============================================================================
// HR (Risorse Umane) — data layer
// ----------------------------------------------------------------------------
// Identità dipendenti = utenti interni reali (backend `/api/hr/employees`,
// profili in hr_profiles). Nessun mock identità, nessun seed demo.
// I dati operativi (documenti, buste paga, ferie, notifiche) restano client-side
// mock (localStorage) in attesa del backend dedicato, ma sono SEMPRE agganciati
// agli id utente reali. Nessun dato demo precaricato.
// ============================================================================
import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";
import { MONTHS_IT } from "@/lib/tasksData";

export { MONTHS_IT };
export const HR_YEAR = 2026;

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const DOC_TYPES = {
  payslip: { label: "Busta paga", tone: "info", requires_ack: true },
  contract: { label: "Contratto", tone: "neutral", requires_ack: true },
  regulation: { label: "Regolamento / Informativa", tone: "warning", requires_ack: true },
  certificate: { label: "Certificato malattia", tone: "error", requires_ack: false },
  permit: { label: "Permesso / Altro", tone: "neutral", requires_ack: false },
};
export const LEAVE_TYPES = {
  ferie: { label: "Ferie", tone: "info" },
  malattia: { label: "Malattia", tone: "error" },
  permesso: { label: "Permesso", tone: "warning" },
};
export const LEAVE_STATUS = {
  pending: { label: "In approvazione", tone: "warning" },
  approved: { label: "Approvata", tone: "ok" },
  rejected: { label: "Rifiutata", tone: "error" },
};
export const CONTRACT_TYPES = ["Indeterminato", "Determinato", "Apprendistato", "Amministratore", "P.IVA / Collaborazione"];
export const DAYS_IT = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

// Funzioni HR ancora solo-localStorage (documenti/cedolini/ferie): disabilitate finché
// non c'è un backend persistente, per non salvare dati HR importanti solo nel browser.
export const HR_LOCAL_FEATURES_ENABLED = false;

// ---------------------------------------------------------------------------
// EMPLOYEE CACHE (backend-backed) + reactive store
// ---------------------------------------------------------------------------
let _employees = [];
let _version = 0;
const subs = new Set();
const notify = () => { _version += 1; subs.forEach((f) => f()); };
export const subscribeHr = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export const useHr = () => useSyncExternalStore((cb) => subscribeHr(cb), () => _version, () => _version);

export async function hydrateHr() {
  try { _employees = await api.get("/hr/employees").then((r) => r.data); }
  catch { _employees = []; }
  notify();
}
export async function hydrateMyHr() {
  try { _employees = [await api.get("/hr/me").then((r) => r.data)]; }
  catch { _employees = []; }
  notify();
}
export function resetHr() { _employees = []; notify(); }

export const listEmployees = () => _employees;
export const getEmployee = (id) => _employees.find((e) => e.id === id) || null;
export const updateEmployee = (id, p) => {
  _employees = _employees.map((e) => (e.id === id ? { ...e, ...p } : e));
  notify();
  api.patch(`/hr/profiles/${id}`, p).catch(() => {});
};
export const setEmployeeShifts = (id, shifts) => updateEmployee(id, { shifts });

// Cedolini (busta paga) + documenti HR — upload reale su object storage
export const uploadPayslips = (files, period) => {
  const fd = new FormData();
  [...files].forEach((f) => fd.append("files", f));
  if (period) fd.append("period", period);
  return api.post("/hr/payslips/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const listPayslips = (userId) => api.get("/hr/payslips", { params: userId ? { user_id: userId } : {} }).then((r) => r.data);
export const listMyPayslips = () => api.get("/hr/me/payslips").then((r) => r.data);
export const assignPayslip = (pid, userId) => api.patch(`/hr/payslips/${pid}/assign`, { user_id: userId }).then((r) => r.data);
export const payslipFileUrl = (pid) => `${api.defaults.baseURL}/hr/payslips/${pid}/file`;
export const uploadHrDocument = (file, userId, title) => {
  const fd = new FormData();
  fd.append("file", file);
  if (userId) fd.append("user_id", userId);
  if (title) fd.append("title", title);
  return api.post("/hr/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const listHrDocuments = (userId) => api.get("/hr/documents", { params: userId ? { user_id: userId } : {} }).then((r) => r.data);
export const hrDocumentFileUrl = (did) => `${api.defaults.baseURL}/hr/documents/${did}/file`;
// L'identità (creazione/rimozione dipendenti) è gestita in Utenti & Permessi.
export const addEmployee = () => null;
export const removeEmployee = () => {};

// ---------------------------------------------------------------------------
// OPERATIONAL MOCK STORE (documenti / ferie / presa visione / notifiche)
// Agganciato agli id utente reali. Nessun seed demo.
// ---------------------------------------------------------------------------
const KEY = "aesthetic-os-hr-ops";
const readOps = () => {
  try { const r = localStorage.getItem(KEY); if (r) return JSON.parse(r); } catch {}
  const s = { documents: [], acks: [], leave: [], notifications: [] };
  localStorage.setItem(KEY, JSON.stringify(s));
  return s;
};
const writeOps = (s) => localStorage.setItem(KEY, JSON.stringify(s));
const mutate = (fn) => { const s = readOps(); fn(s); writeOps(s); notify(); };

// ---------------------------------------------------------------------------
// DOCUMENTS
// ---------------------------------------------------------------------------
export const listDocuments = (employeeId, type) => readOps().documents
  .filter((d) => (!employeeId || d.employee_id === employeeId) && (!type || d.type === type))
  .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));

export const addDocument = (d) => { const id = uid("doc"); mutate((s) => s.documents.push({ id, uploaded_at: now(), requires_ack: DOC_TYPES[d.type]?.requires_ack ?? true, scope: "single", ...d })); return id; };
export const removeDocument = (id) => mutate((s) => { s.documents = s.documents.filter((d) => d.id !== id); s.acks = s.acks.filter((a) => a.document_id !== id); });

// Split cedolini "AI" (SIMULATO): una busta paga per ogni dipendente attivo.
export const splitCedolini = ({ month, year, fileName, uploadedBy }) => {
  const emps = _employees;
  const created = [];
  mutate((s) => {
    emps.forEach((e) => {
      const id = uid("doc");
      const doc = { id, employee_id: e.id, type: "payslip", name: `Cedolino ${MONTHS_IT[month - 1]} ${year} — ${e.name}.pdf`, month, year, uploaded_by: uploadedBy, uploaded_at: now(), scope: "single", requires_ack: true, note: `Da ${fileName} (split AI simulato)`, source_file: fileName };
      s.documents.push(doc);
      created.push({ ...doc, employeeName: e.name });
      s.notifications.push({ id: uid("ntf"), audience: "employee", employee_id: e.id, type: "document", title: "Nuovo cedolino disponibile", body: `Cedolino ${MONTHS_IT[month - 1]} ${year}`, read: false, created_at: now() });
    });
  });
  return created;
};

export const addDocumentToEmployees = ({ employeeIds, type, name, note, year, uploadedBy }) => {
  const batch_id = uid("batch");
  const scope = employeeIds.length > 1 ? "all" : "single";
  mutate((s) => {
    employeeIds.forEach((eid) => {
      s.documents.push({ id: uid("doc"), employee_id: eid, type, name, month: null, year: year ?? null, uploaded_by: uploadedBy, uploaded_at: now(), scope, requires_ack: DOC_TYPES[type]?.requires_ack ?? true, note: note || "", batch_id });
      s.notifications.push({ id: uid("ntf"), audience: "employee", employee_id: eid, type: "document", title: "Nuovo documento caricato", body: name, read: false, created_at: now() });
    });
  });
  return batch_id;
};

// ---------------------------------------------------------------------------
// ACKNOWLEDGEMENTS (presa visione)
// ---------------------------------------------------------------------------
export const getAck = (documentId, employeeId) => readOps().acks.find((a) => a.document_id === documentId && a.employee_id === employeeId);
export const hasAck = (documentId, employeeId) => !!getAck(documentId, employeeId);
export const ackDocument = (documentId, employeeId) => {
  if (hasAck(documentId, employeeId)) return;
  mutate((s) => s.acks.push({ id: uid("ack"), document_id: documentId, employee_id: employeeId, viewed_at: now() }));
};
export const employeeAckLog = (employeeId) => {
  const s = readOps();
  return s.documents.filter((d) => d.employee_id === employeeId).map((d) => ({ doc: d, ack: s.acks.find((a) => a.document_id === d.id && a.employee_id === employeeId) || null }));
};

// ---------------------------------------------------------------------------
// LEAVE REQUESTS (ferie / malattia / permessi)
// ---------------------------------------------------------------------------
export const listLeave = (employeeId) => readOps().leave
  .filter((l) => !employeeId || l.employee_id === employeeId)
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
export const listPendingLeave = () => readOps().leave.filter((l) => l.status === "pending");

export const addLeaveRequest = ({ employeeId, type, dates, hours, reason, retroactive }) => {
  const emp = getEmployee(employeeId);
  mutate((s) => {
    s.leave.push({ id: uid("lv"), employee_id: employeeId, type, dates, hours: hours || null, reason: reason || "", status: "pending", retroactive: !!retroactive, created_at: now(), decided_at: null, decided_by: null });
    s.notifications.push({ id: uid("ntf"), audience: "master", employee_id: employeeId, type: "leave_request", title: `Richiesta ${LEAVE_TYPES[type]?.label} da ${emp?.name || ""}`, body: `${dates.length} giorno/i`, read: false, created_at: now() });
  });
};
export const decideLeave = (id, status, decidedBy) => mutate((s) => {
  const i = s.leave.findIndex((l) => l.id === id);
  if (i > -1) {
    s.leave[i] = { ...s.leave[i], status, decided_at: now(), decided_by: decidedBy };
    s.notifications.push({ id: uid("ntf"), audience: "employee", employee_id: s.leave[i].employee_id, type: "leave_decision", title: `Richiesta ${status === "approved" ? "approvata" : "rifiutata"}`, body: `${LEAVE_TYPES[s.leave[i].type]?.label} · ${s.leave[i].dates.length} giorno/i`, read: false, created_at: now() });
  }
});

// ---------------------------------------------------------------------------
// NOTIFICATIONS (in-app; email simulate)
// ---------------------------------------------------------------------------
export const listNotifications = ({ audience, employeeId }) => readOps().notifications
  .filter((n) => n.audience === audience && (audience === "master" || n.employee_id === employeeId))
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
export const unreadCount = (args) => listNotifications(args).filter((n) => !n.read).length;
export const markNotificationRead = (id) => mutate((s) => { const i = s.notifications.findIndex((n) => n.id === id); if (i > -1) s.notifications[i].read = true; });
export const markAllNotificationsRead = ({ audience, employeeId }) => mutate((s) => s.notifications.forEach((n) => { if (n.audience === audience && (audience === "master" || n.employee_id === employeeId)) n.read = true; }));

// ---------------------------------------------------------------------------
// KPI
// ---------------------------------------------------------------------------
export const hrKpis = () => {
  const ops = readOps();
  return {
    employees: _employees.length,
    payslips: ops.documents.filter((d) => d.type === "payslip").length,
    pendingLeave: ops.leave.filter((l) => l.status === "pending").length,
    laborMonthly: _employees.reduce((a, e) => a + (e.hourly_cost || 0) * (e.weekly_hours || 40) * 4, 0),
  };
};
